import { createMemo, createRoot, createSignal } from "solid-js"
import { createStore, produce } from "solid-js/store"
import {
  fetchBootstrap,
  fetchRouteSchedule,
  fetchRouteTrips,
  fetchTripDetail,
  fetchVehicles,
} from "../lib/api"
import type {
  AgencyInfo,
  LiveVehicle,
  RouteInfo,
  RouteSchedule,
  StopPoint,
  SystemStats,
  TripDetail,
  View,
} from "../lib/types"
import { buildUniqueColorMap } from "../lib/colors"
import { mapController } from "../map/controller"

function createAppStore() {
  const [agencies, setAgencies] = createStore<Record<string, AgencyInfo>>({})
  const [shapeColors, setShapeColors] = createSignal<Record<string, string>>({})
  const [shapeRoutes, setShapeRoutes] = createSignal<Record<string, string>>({})
  const [enabledRoutes, setEnabledRoutes] = createStore<Record<string, boolean>>({})
  const [collapsedAgencies, setCollapsedAgencies] = createStore<Record<string, boolean>>({})
  const [vehicles, setVehicles] = createSignal<LiveVehicle[]>([])
  const [stats, setStats] = createSignal<SystemStats | null>(null)
  const [vehicleUpdatedAt, setVehicleUpdatedAt] = createSignal(0)
  const [search, setSearch] = createSignal("")
  const [view, setView] = createSignal<View>("browse")
  const [selectedRouteId, setSelectedRouteId] = createSignal<string | null>(null)
  const [selectedVehicleId, setSelectedVehicleId] = createSignal<string | null>(null)
  const [sidebarCollapsed, setSidebarCollapsed] = createSignal(false)
  const [sidebarExpanded, setSidebarExpanded] = createSignal(false)
  const [uniqueColors, setUniqueColors] = createSignal(
    typeof localStorage !== "undefined" && localStorage.getItem("live-seattle:uniqueColors") === "1"
  )
  const [schedule, setSchedule] = createSignal<RouteSchedule | null>(null)
  const [tripDetail, setTripDetail] = createSignal<TripDetail | null>(null)
  const [loadingDetail, setLoadingDetail] = createSignal(false)
  const [shapesReady, setShapesReady] = createSignal(false)
  const [followVehicle, setFollowVehicle] = createSignal(false)

  let routeLoadToken = 0
  let vehicleLoadToken = 0
  let pollTimer: number | undefined

  const vehicleMap = createMemo(() =>
    Object.fromEntries(vehicles().map((v) => [v.id, v])) as Record<string, LiveVehicle>
  )

  const selectedVehicle = createMemo(() => {
    const id = selectedVehicleId()
    return id ? vehicleMap()[id] : undefined
  })

  const selectedRoute = createMemo(() => {
    const id = selectedRouteId()
    if (!id) return null
    for (const agency of Object.values(agencies)) {
      const route = agency.routes.find((r) => r.id === id)
      if (route) return { agency, route }
    }
    return null
  })

  const filteredAgencies = createMemo(() => {
    const q = search().trim().toLowerCase()
    const list = Object.values(agencies)
    if (!q) return list
    return list
      .map((agency) => ({
        ...agency,
        routes: agency.routes.filter((route) => {
          const hay = `${route.shortName || ""} ${route.description} ${route.id} ${agency.name}`.toLowerCase()
          return hay.includes(q)
        }),
      }))
      .filter((agency) => agency.routes.length > 0)
  })

  const enabledRouteList = createMemo(() =>
    Object.entries(enabledRoutes)
      .filter(([, on]) => on)
      .map(([id]) => id)
  )

  const agencyEnabledState = createMemo(() => {
    const result: Record<string, "all" | "some" | "none"> = {}
    for (const agency of Object.values(agencies)) {
      const total = agency.routes.length
      if (!total) {
        result[agency.id] = "none"
        continue
      }
      const on = agency.routes.filter((r) => enabledRoutes[r.id]).length
      result[agency.id] = on === 0 ? "none" : on === total ? "all" : "some"
    }
    return result
  })

  const vehiclesForSelectedRoute = createMemo(() => {
    const routeId = selectedRouteId()
    if (!routeId) return [] as LiveVehicle[]
    return vehicles()
      .filter((v) => v.routeId === routeId)
      .slice()
      .sort((a, b) => (a.delay || 0) - (b.delay || 0))
  })

  const uniqueColorMap = createMemo(() => {
    const routes = Object.values(agencies).flatMap((a) => a.routes)
    return buildUniqueColorMap(routes)
  })

  function routeColor(routeId: string, fallback?: string) {
    if (uniqueColors()) {
      const mapped = uniqueColorMap()[routeId]
      if (mapped) return mapped.color
    }
    const found = findRoute(routeId)
    return found?.route.color || fallback || "ffffff"
  }

  function routeTextColor(routeId: string, fallback?: string) {
    if (uniqueColors()) {
      const mapped = uniqueColorMap()[routeId]
      if (mapped) return mapped.textColor
    }
    const found = findRoute(routeId)
    return found?.route.textColor || fallback || "111111"
  }

  function displayShapeColors() {
    const routes = shapeRoutes()
    const base = shapeColors()
    if (!uniqueColors()) return base
    const next: Record<string, string> = {}
    for (const [shapeId, routeId] of Object.entries(routes)) {
      next[shapeId] = routeColor(routeId, base[shapeId])
    }
    return next
  }

  function applyColorsToMap() {
    mapController.applyShapeColors(displayShapeColors())
    mapController.syncVehicles()
  }

  function toggleUniqueColors() {
    const next = !uniqueColors()
    setUniqueColors(next)
    try {
      localStorage.setItem("live-seattle:uniqueColors", next ? "1" : "0")
    } catch { }
    applyColorsToMap()
  }

  const tripStops = createMemo((): StopPoint[] => {
    const detail = tripDetail()
    const vehicle = selectedVehicle()
    if (!detail?.stops?.length) return []

    const nextIdx = vehicle?.nextStopId
      ? detail.stops.findIndex((s) => s.id === vehicle.nextStopId)
      : -1

    return detail.stops.map((stop, index) => {
      let passed = !!stop.passed
      if (vehicle?.distanceAlongTrip != null && stop.distanceAlongTrip != null) {
        passed = stop.distanceAlongTrip < vehicle.distanceAlongTrip - 25
      } else if (nextIdx >= 0) {
        passed = index < nextIdx
      }
      return {
        ...stop,
        next: nextIdx >= 0 ? index === nextIdx : stop.id === vehicle?.nextStopId,
        passed,
      }
    })
  })

  function isRouteEnabled(routeId: string) {
    return !!enabledRoutes[routeId]
  }

  function setRouteEnabled(routeId: string, enabled: boolean) {
    setEnabledRoutes(routeId, enabled)
    mapController.syncShapes()
    mapController.syncVehicles()
  }

  function toggleRoute(routeId: string) {
    setRouteEnabled(routeId, !isRouteEnabled(routeId))
  }

  function setAgencyEnabled(agencyId: string, enabled: boolean) {
    const agency = agencies[agencyId]
    if (!agency) return
    setEnabledRoutes(
      produce((draft) => {
        for (const route of agency.routes) draft[route.id] = enabled
      })
    )
    mapController.syncShapes()
    mapController.syncVehicles()
  }

  function toggleAgency(agencyId: string) {
    const state = agencyEnabledState()[agencyId]
    setAgencyEnabled(agencyId, state !== "all")
  }

  function isAgencyCollapsed(agencyId: string) {
    // while searching, keep matches expanded so results aren't hidden
    if (search().trim()) return false
    return !!collapsedAgencies[agencyId]
  }

  function toggleAgencyCollapsed(agencyId: string) {
    setCollapsedAgencies(agencyId, !collapsedAgencies[agencyId])
  }

  function setAgencyCollapsed(agencyId: string, collapsed: boolean) {
    setCollapsedAgencies(agencyId, collapsed)
  }

  async function bootstrap() {
    const data = await fetchBootstrap()
    setAgencies(data.agencies)
    setShapeColors(data.shapeColors)
    setShapeRoutes(data.shapeRoutes)
    await mapController.loadShapes(data.shapeColors, data.shapeRoutes)
    setShapesReady(true)
    applyColorsToMap()
    mapController.syncShapes()
  }

  async function refreshVehicles() {
    const data = await fetchVehicles()
    setVehicles(data.vehicles)
    setStats(data.stats)
    setVehicleUpdatedAt(data.updatedAt)
    mapController.syncVehicles()

    if (followVehicle() && selectedVehicleId()) {
      const vehicle = vehicleMap()[selectedVehicleId()!]
      if (vehicle) mapController.panTo(vehicle.lat, vehicle.lon)
    }

    if (view() === "vehicle" && selectedVehicle()) {
      const v = selectedVehicle()!
      mapController.showStops(tripStops(), { color: routeColor(v.routeId, v.routeColor) })
    }
  }

  function startPolling() {
    refreshVehicles().catch(console.error)
    if (pollTimer) window.clearInterval(pollTimer)
    pollTimer = window.setInterval(() => {
      refreshVehicles().catch(console.error)
    }, 10000)
  }

  async function openRoute(routeId: string, fit = true) {
    setView("route")
    setSelectedRouteId(routeId)
    setSelectedVehicleId(null)
    setFollowVehicle(false)
    setRouteEnabled(routeId, true)
    setSchedule(null)
    setTripDetail(null)
    mapController.clearStops()
    if (fit) mapController.fitRoute(routeId)
    setLoadingDetail(true)

    const token = ++routeLoadToken
    try {
      const [sched, trips] = await Promise.all([
        fetchRouteSchedule(routeId),
        fetchRouteTrips(routeId),
      ])
      if (token !== routeLoadToken) return
      setSchedule(sched)
      // keep trips payload available via schedule directions; vehicles come from live poll
      void trips
      const direction = sched.directions?.[0]
      if (direction?.stops?.length) {
        mapController.showStops(direction.stops, { color: routeColor(routeId) })
      }
    } catch (err) {
      console.error(err)
      if (token !== routeLoadToken) return
      setSchedule(null)
    } finally {
      if (token === routeLoadToken) setLoadingDetail(false)
    }
  }

  async function openVehicle(vehicleId: string, follow = true) {
    const vehicle = vehicleMap()[vehicleId]
    setView("vehicle")
    setSelectedVehicleId(vehicleId)
    if (vehicle?.routeId) {
      setSelectedRouteId(vehicle.routeId)
      setRouteEnabled(vehicle.routeId, true)
    }
    setFollowVehicle(follow)
    if (follow && vehicle) mapController.focusVehicle(vehicle)
    setTripDetail(null)
    setLoadingDetail(true)

    if (!vehicle?.obaTripId) {
      setLoadingDetail(false)
      mapController.clearStops()
      return
    }

    const token = ++vehicleLoadToken
    try {
      const detail = await fetchTripDetail(vehicle.obaTripId)
      if (token !== vehicleLoadToken) return
      setTripDetail(detail)
      mapController.showStops(
        detail.stops.map((stop, index) => {
          const nextIdx = detail.stops.findIndex((s) => s.id === vehicle.nextStopId)
          return {
            ...stop,
            next: nextIdx >= 0 ? index === nextIdx : stop.id === vehicle.nextStopId,
            passed:
              vehicle.distanceAlongTrip != null && stop.distanceAlongTrip != null
                ? stop.distanceAlongTrip < vehicle.distanceAlongTrip - 25
                : nextIdx >= 0
                  ? index < nextIdx
                  : stop.passed,
          }
        }),
        { color: routeColor(vehicle.routeId, vehicle.routeColor) }
      )
    } catch (err) {
      console.error(err)
      if (token !== vehicleLoadToken) return
      setTripDetail(null)
    } finally {
      if (token === vehicleLoadToken) setLoadingDetail(false)
    }
  }

  function goBack() {
    if (view() === "vehicle" && selectedRouteId()) {
      openRoute(selectedRouteId()!, false)
      return
    }
    setView("browse")
    setSelectedRouteId(null)
    setSelectedVehicleId(null)
    setFollowVehicle(false)
    setSchedule(null)
    setTripDetail(null)
    mapController.clearStops()
  }

  function findRoute(routeId: string): { agency: AgencyInfo; route: RouteInfo } | null {
    for (const agency of Object.values(agencies)) {
      const route = agency.routes.find((r) => r.id === routeId)
      if (route) return { agency, route }
    }
    return null
  }

  function agencyStats(agencyId: string) {
    return stats()?.agencies.find((a) => a.agencyId === agencyId)
  }

  return {
    agencies,
    shapeColors,
    shapeRoutes,
    enabledRoutes,
    vehicles,
    stats,
    vehicleUpdatedAt,
    search,
    setSearch,
    view,
    selectedRouteId,
    selectedVehicleId,
    selectedVehicle,
    selectedRoute,
    filteredAgencies,
    enabledRouteList,
    agencyEnabledState,
    vehiclesForSelectedRoute,
    tripStops,
    schedule,
    tripDetail,
    loadingDetail,
    shapesReady,
    sidebarCollapsed,
    setSidebarCollapsed,
    sidebarExpanded,
    setSidebarExpanded,
    toggleSidebarExpanded() {
      const next = !sidebarExpanded()
      setSidebarExpanded(next)
      if (next) setSidebarCollapsed(false)
    },
    uniqueColors,
    toggleUniqueColors,
    routeColor,
    routeTextColor,
    followVehicle,
    setFollowVehicle,
    isRouteEnabled,
    setRouteEnabled,
    toggleRoute,
    setAgencyEnabled,
    toggleAgency,
    isAgencyCollapsed,
    toggleAgencyCollapsed,
    setAgencyCollapsed,
    bootstrap,
    refreshVehicles,
    startPolling,
    openRoute,
    openVehicle,
    goBack,
    findRoute,
    agencyStats,
  }
}

export const app = createRoot(createAppStore)
